import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';

import { ChartConfig, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { EmptyState, SectionCard, formatDate } from '../../../components/design-system/admin';
import { PROVIDER_NAMES, type CostProviderId, type CostSeriesPoint, type ProviderView } from '../../../common/models/platform-costs.model';
import { money } from './cost-format';

/** The chart ramp, darkest first: the biggest spender gets the most ink. */
const RAMP = ['var(--chart-5)', 'var(--chart-4)', 'var(--chart-3)', 'var(--chart-2)', 'var(--chart-1)'];

/** Order providers by what they spent in the window, largest first. */
export function rankProviders(providers: ProviderView[]): CostProviderId[] {
  return providers
    .filter((p) => p.status !== 'unconfigured' || p.windowUsd > 0)
    .sort((a, b) => b.windowUsd - a.windowUsd)
    .map((p) => p.provider);
}

export const colourOf = (ranked: CostProviderId[], id: CostProviderId) => RAMP[Math.max(ranked.indexOf(id), 0) % RAMP.length];

/** Spend per day, every provider stacked. Monochrome: shades of ink ranked by spend. */
export function SpendChart({ series, providers, className }: { series: CostSeriesPoint[]; providers: ProviderView[]; className?: string }) {
  const { t } = useTranslation();
  const ranked = rankProviders(providers);
  const config: ChartConfig = Object.fromEntries(ranked.map((id) => [id, { label: PROVIDER_NAMES[id], color: colourOf(ranked, id) }]));
  const short = (d: string) => new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(new Date(`${d}T00:00:00Z`));
  const empty = !series.some((p) => p.total > 0);

  return (
    <SectionCard title={t('ADMIN_COSTS.CHART.TITLE')} description={t('ADMIN_COSTS.CHART.DESCRIPTION')} className={className} testId="costs-chart">
      {empty ? (
        <EmptyState title={t('ADMIN_COSTS.CHART.NONE')} className="h-64" />
      ) : (
        <ChartContainer config={config} className="aspect-auto h-64 w-full">
          <BarChart data={series} margin={{ left: -8, right: 4, top: 8, bottom: 0 }} barCategoryGap={series.length > 40 ? 1 : 3}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} tickFormatter={short} />
            <YAxis tickLine={false} axisLine={false} width={52} tickFormatter={(v) => `$${Math.round(v)}`} />
            <ChartTooltip
              cursor={{ fill: 'var(--muted)', opacity: 0.5 }}
              content={
                <ChartTooltipContent
                  labelFormatter={(_, payload) => {
                    const point = payload?.[0]?.payload as CostSeriesPoint | undefined;
                    return point ? `${formatDate(`${point.date}T00:00:00Z`)} · ${money(point.total)}` : '';
                  }}
                  formatter={(value, name) => (
                    <div className="flex w-full items-center justify-between gap-3">
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <span className="size-2 rounded-[2px]" style={{ background: colourOf(ranked, name as CostProviderId) }} />
                        {PROVIDER_NAMES[name as CostProviderId] || name}
                      </span>
                      <span className="font-medium tabular-nums">{money(Number(value))}</span>
                    </div>
                  )}
                />
              }
            />
            <ChartLegend content={<ChartLegendContent />} />
            {[...ranked].reverse().map((id, i, all) => (
              <Bar key={id} dataKey={id} stackId="spend" fill={`var(--color-${id})`} radius={i === all.length - 1 ? [3, 3, 0, 0] : 0} isAnimationActive={false} />
            ))}
          </BarChart>
        </ChartContainer>
      )}
    </SectionCard>
  );
}
